import api from './axiosInstance';

export interface StockItem {
  id: number;
  stockId: number;
  code: string;
  name: string;
  brand: string;
  price: number;
  quantity: number;
  deliveryDate?: string | null;
  description?: string | null;
  isActive: boolean;
  created: string;
  updated: string;
  discountPercentage?: number | null;
  newUntilDate?: string | null;
  imagePath?: string | null;
}

export interface PagedMeta {
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface GetItemsParams {
  search?: string;
  stockId?: number;
  includeInactive?: boolean;
  page?: number;
  pageSize?: number;
}

function parsePaginationFromHeaders(headers: any): PagedMeta | null {
  const raw = headers['x-pagination'] || headers['X-Pagination'];
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return {
      totalCount: parsed.totalCount ?? parsed.TotalCount,
      page: parsed.page ?? parsed.Page,
      pageSize: parsed.pageSize ?? parsed.PageSize,
      totalPages: parsed.totalPages ?? parsed.TotalPages,
      hasPreviousPage: parsed.hasPreviousPage ?? parsed.HasPreviousPage,
      hasNextPage: parsed.hasNextPage ?? parsed.HasNextPage,
    } as PagedMeta;
  } catch {
    return null;
  }
}

export const warehouseApi = {
  getItemsPaged: async (params: GetItemsParams = {}): Promise<{ data: StockItem[]; meta: PagedMeta | null; }> => {
    const response = await api.get('Warehouse/items/paged', { params: { stockId: 1, includeInactive: false, page: 1, pageSize: 20, ...params } });
    const meta = parsePaginationFromHeaders(response.headers);
    return { data: response.data as StockItem[], meta };
  },

  getAllItems: async (stockId: number = 1, includeInactive: boolean = false): Promise<StockItem[]> => {
    const response = await api.get('Warehouse/items', { params: { stockId, includeInactive } });
    return response.data as StockItem[];
  },

  searchItems: async (searchString: string, stockId: number = 1): Promise<StockItem[]> => {
    const response = await api.get('Warehouse/search', { params: { searchString, stockId } });
    return response.data as StockItem[];
  },

  getItemById: async (id: number): Promise<StockItem> => {
    const response = await api.get(`Warehouse/items/${id}`);
    return response.data as StockItem;
  },

  addItem: async (item: Omit<StockItem, 'id' | 'created' | 'updated'>): Promise<StockItem> => {
    const response = await api.post('Warehouse/items', item);
    return response.data as StockItem;
  },

  updateItem: async (id: number, item: StockItem): Promise<void> => {
    await api.put(`Warehouse/items/${id}`, item);
  },

  deleteItem: async (id: number): Promise<void> => {
    await api.delete(`Warehouse/items/${id}`);
  },

  uploadImage: async (id: number, file: File): Promise<{ url: string }> => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`Warehouse/items/${id}/image`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data as { url: string };
  },
};

export default warehouseApi;


